import {
  CaretDown,
  Check,
  DotsThree,
  PencilSimple,
  Plus,
  PushPin,
  Stack,
  Trash,
  UsersThree,
} from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import type { ViewDto } from "@plannello/shared";
import { Link, useNavigate } from "@tanstack/react-router";
import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  Button as AriaButton,
  Collection,
  Dialog,
  DialogTrigger,
  GridList,
  GridListHeader,
  GridListItem,
  GridListSection,
} from "react-aria-components";
import { Button, IconButton, Menu, MenuItem, MenuTrigger, Popover, SearchField, Spinner } from "@/components/ui";
import { useCatalog } from "@/data/CatalogProvider";
import type { Product } from "@/data/types";
import { pushUndo } from "@/data/undo";
import { cx } from "@/lib/cx";
import { toast } from "@/lib/toast";
import { createView, deleteView, setPinned, useProductViews } from "./api";
import { isShared, openSaveView, useViewsMenuRequests, viewIcon, viewName } from "./viewMeta";
import { viewAppliesTo, viewLink } from "./viewState";

type Section = { id: string; title: string; views: ViewDto[] };

/**
 * The views of a product in one searchable popover: Standard (built-ins), My views (personal)
 * and Shared. Type to filter, arrows to move, Enter opens. Each row has a pin toggle (sidebar) and,
 * for views you may edit, rename and delete (with undo). The trigger names the loaded view.
 */
export function ViewsMenu({ product, currentId }: { product: Product; currentId: string | undefined }) {
  const catalog = useCatalog();
  const navigate = useNavigate();
  const q = useProductViews(product.key);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);
  // V or the palette open the menu.
  const requests = useViewsMenuRequests();
  const seen = useRef(requests);
  useEffect(() => {
    if (requests !== seen.current) {
      seen.current = requests;
      setOpen(true);
    }
  }, [requests]);

  const views = useMemo(
    () => (q.data ?? []).filter((v) => viewAppliesTo(v, product.key, catalog)),
    [q.data, product.key, catalog],
  );
  const current = views.find((v) => v.id === currentId);
  const sections = useMemo<Section[]>(() => {
    const f = filter.trim().toLocaleLowerCase();
    const match = (v: ViewDto) => !f || viewName(v).toLocaleLowerCase().includes(f);
    const all: Section[] = [
      { id: "builtin", title: m.views_section_standard(), views: views.filter((v) => v.builtin && match(v)) },
      {
        id: "mine",
        title: m.views_section_mine(),
        views: views.filter((v) => !v.builtin && v.scope === "personal" && match(v)),
      },
      { id: "shared", title: m.views_section_shared(), views: views.filter((v) => isShared(v) && match(v)) },
    ];
    return all.filter((s) => s.views.length > 0);
  }, [views, filter]);
  const count = sections.reduce((n, s) => n + s.views.length, 0);

  const openView = (v: ViewDto) => {
    setOpen(false);
    const link = viewLink(v, product.key);
    void navigate({ to: link.to, params: link.params, search: link.search as never });
  };

  const remove = async (v: ViewDto) => {
    try {
      await deleteView(v.id);
    } catch {
      toast.show({ title: m.toast_update_failed(), tone: "danger" });
      return;
    }
    if (v.id === currentId)
      void navigate({ to: ".", search: ((p: { view?: string }) => ({ ...p, view: undefined })) as never });
    const restore = () =>
      void createView({
        name: v.name,
        scope: v.scope,
        scopeId: v.scopeId,
        query: v.query,
        layout: v.layout,
        display: v.display,
        pinned: v.pinned,
      }).catch(() => toast.show({ title: m.toast_update_failed(), tone: "danger" }));
    toast.undo(m.views_deleted({ name: v.name }), pushUndo(restore), m.common_undo());
  };

  const onSearchKey = (e: KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      gridRef.current?.querySelector<HTMLElement>('[role="row"]')?.focus();
    }
  };

  const CurrentIcon = current ? viewIcon(current) : Stack;
  return (
    <DialogTrigger
      isOpen={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setFilter("");
      }}
    >
      <Button
        variant="secondary"
        size="sm"
        aria-label={current ? `${m.views_menu()}: ${viewName(current)}` : m.views_menu()}
      >
        <CurrentIcon size={14} aria-hidden="true" className="text-fg-subtle" />
        <span className="max-w-44 truncate-title">{current ? viewName(current) : m.views_menu()}</span>
        {current && isShared(current) ? <UsersThree size={12} aria-hidden="true" className="text-fg-subtle" /> : null}
        <CaretDown size={12} weight="bold" aria-hidden="true" className="text-fg-subtle" />
      </Button>
      <Popover placement="bottom end" className="w-88 max-w-[calc(100vw-24px)] p-0">
        <Dialog aria-label={m.views_menu()} className="flex max-h-[min(520px,70vh)] flex-col outline-none">
          <div className="border-b border-line p-2">
            <SearchField
              label={m.views_search()}
              placeholder={m.views_search()}
              value={filter}
              onChange={setFilter}
              onKeyDown={onSearchKey}
              autoFocus
            />
          </div>
          <div className="scroll-thin min-h-0 flex-1 overflow-y-auto p-1">
            {q.isPending ? (
              <div className="flex items-center gap-2 px-2 py-3 text-sm text-fg-muted">
                <Spinner /> {m.common_loading()}
              </div>
            ) : count === 0 ? (
              <p className="px-2 py-3 text-sm text-fg-muted">{m.views_no_match({ query: filter })}</p>
            ) : (
              <GridList
                ref={gridRef}
                aria-label={m.views_menu()}
                onAction={(key) => {
                  const v = views.find((x) => x.id === String(key));
                  if (v) openView(v);
                }}
                className="outline-none"
              >
                {sections.map((s) => (
                  <GridListSection key={s.id} id={s.id} className="pb-1">
                    <GridListHeader className="px-2 pt-2 pb-1 text-2xs font-medium text-fg-subtle">
                      {s.title}
                    </GridListHeader>
                    <Collection items={s.views}>
                      {(v) =>
                        viewRow({
                          view: v,
                          isCurrent: v.id === currentId,
                          onDelete: () => void remove(v),
                          onEdit: () => {
                            setOpen(false);
                            openSaveView({ mode: "edit", view: v });
                          },
                        })
                      }
                    </Collection>
                  </GridListSection>
                ))}
              </GridList>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 border-t border-line p-1.5">
            <Button
              variant="ghost"
              size="sm"
              onPress={() => {
                setOpen(false);
                openSaveView({ mode: "create" });
              }}
            >
              <Plus size={14} aria-hidden="true" />
              {m.views_save_current()}
            </Button>
            <Link
              to="/p/$productKey/views"
              params={{ productKey: product.key }}
              onClick={() => setOpen(false)}
              className="rounded-control px-2 py-1 text-xs font-medium whitespace-nowrap text-accent-text outline-hidden hover:underline focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-focus"
            >
              {m.views_manage()}
            </Link>
          </div>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}

/** One view row; a plain function so the collection sees the GridListItem (see PinnedViews). */
function viewRow({
  view,
  isCurrent,
  onEdit,
  onDelete,
}: {
  view: ViewDto;
  isCurrent: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const Icon = viewIcon(view);
  const name = viewName(view);
  return (
    <GridListItem
      id={view.id}
      textValue={name}
      className={cx(
        "group/vrow flex min-h-(--control-h-md) cursor-default items-center gap-2 rounded-control pr-1 pl-2 text-sm text-fg outline-hidden",
        "data-hovered:bg-hover data-focused:bg-active data-focus-visible:outline-2 data-focus-visible:outline-solid data-focus-visible:-outline-offset-2 data-focus-visible:outline-focus",
      )}
    >
      <Icon size={16} aria-hidden="true" className="shrink-0 text-fg-muted" />
      <span className={cx("min-w-0 flex-1 truncate-title", isCurrent && "font-medium")} title={name}>
        {name}
      </span>
      {isShared(view) ? (
        <span className="flex shrink-0 items-center text-fg-subtle" title={m.views_shared()}>
          <UsersThree size={14} aria-hidden="true" />
          <span className="sr-only">{m.views_shared()}</span>
        </span>
      ) : null}
      {isCurrent ? (
        <>
          <Check size={14} aria-hidden="true" className="shrink-0 text-accent-text" />
          <span className="sr-only">{m.views_current()}</span>
        </>
      ) : null}
      <AriaButton
        aria-label={view.pinned ? m.views_unpin({ name }) : m.views_pin({ name })}
        aria-pressed={view.pinned}
        onPress={() =>
          void setPinned(view, !view.pinned).catch(() => toast.show({ title: m.toast_update_failed(), tone: "danger" }))
        }
        className={cx(
          "flex size-6 shrink-0 items-center justify-center rounded-control outline-hidden transition-ui",
          "data-hovered:bg-active data-focus-visible:outline-2 data-focus-visible:outline-solid data-focus-visible:outline-focus",
          view.pinned
            ? "text-accent-text"
            : "text-fg-subtle opacity-0 group-data-hovered/vrow:opacity-100 group-data-focused/vrow:opacity-100 data-focus-visible:opacity-100",
        )}
      >
        <PushPin size={14} weight={view.pinned ? "fill" : "regular"} aria-hidden="true" />
      </AriaButton>
      {view.canEdit ? (
        <MenuTrigger>
          <IconButton
            aria-label={m.views_actions({ name })}
            icon={DotsThree}
            size="sm"
            noTooltip
            className="opacity-0 group-data-hovered/vrow:opacity-100 group-data-focused/vrow:opacity-100 data-focus-visible:opacity-100"
          />
          <Menu aria-label={m.views_actions({ name })} placement="bottom end">
            <MenuItem id="edit" icon={PencilSimple} onAction={onEdit}>
              {m.views_rename_share()}
            </MenuItem>
            <MenuItem id="delete" icon={Trash} tone="danger" onAction={onDelete}>
              {m.views_delete()}
            </MenuItem>
          </Menu>
        </MenuTrigger>
      ) : null}
    </GridListItem>
  );
}
